import {
    Paper,
    Typography,
    TextField,
    FormControlLabel,
    Switch,
    Button,
    Stack,
    Box,
    Alert,
    Divider,
    DialogActions,
    DialogContent,
    Dialog,
    DialogTitle,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { useNavigate, useParams } from "react-router-dom";
import { Localized } from '@fluent/react';
import { SimilarityRequirementDetails, UrsRequirementsSimilarityValidationResult, validateUrsRequirementsSimiliarity } from '../../urstemplates/ursTemplatesSlice';
import { useAppDispatch } from '../../../app/hooks';
import { showError, showSuccess } from '../../notifications/notificationsSlice';
import { BottomAppBar } from '../../../app/BottomAppBar';
import { SimilarityResultsPanel } from './SimilarityResultsPanel';
import SettingsIcon from '@mui/icons-material/Settings';
import { CloseButton } from '../../../app/components/CloseButton';

export const UrsRequirementSimilarityValidator = () => {
    const { id: paramId } = useParams();
    const documentId = paramId ?? ''
    const dispatch = useAppDispatch()
    const navigate = useNavigate();

    const [useSbert, setUseSbert] = useState(true);
    const [useLevenshtein, setUseLevenshtein] = useState(true);
    const [sbertMinSimilarity, setSbertMinSimilarity] = useState<number | "">(80);
    const [levenshteinMinSimilarity, setLevenshteinMinSimilarity] = useState<number | "">(40);
    const [topSbertResults, setTopSbertResults] = useState<number | "">(10);
    const [topLevenshteinResults, setTopLevenshteinResults] = useState<number | "">(10);

    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")
    const [results, setResults] = useState<UrsRequirementsSimilarityValidationResult[]>([]);
    const [optionsOpen, setOptionsOpen] = useState(false);

    const handleBackButtonClick = useCallback(() => {
        navigate(-1)
    }, [navigate])

    const handleValidateClick = async () => {
        setSaving(true)
        setServerError("")
        const payload = {
            documentId,
            useSbert,
            sbertMinSimilarity: sbertMinSimilarity === '' ? 0 : sbertMinSimilarity,
            topSbertResults: topSbertResults === '' ? 0 : topSbertResults,
            useLevenshtein,
            levenshteinMinSimilarity:
                levenshteinMinSimilarity === '' ? 0 : levenshteinMinSimilarity,
            topLevenshteinResults:
                topLevenshteinResults === '' ? 0 : topLevenshteinResults,
        };

        try {
            const result =
                await dispatch(validateUrsRequirementsSimiliarity(payload))

            if (validateUrsRequirementsSimiliarity.fulfilled.match(result)) {
                const results = result.payload as UrsRequirementsSimilarityValidationResult[];
                setResults(results);
                dispatch(showSuccess("saved"))
            }
            else {
                let errorMsg = "error"
                if (result.payload) {
                    if (result.payload.kind === 'http') {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = problem.title
                        }
                    }
                }
                setServerError(errorMsg)
            }
        }
        catch (error) {
            dispatch(showError("error"))
        }
        finally {
            setSaving(false)
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '80vh'
            }}
        >
            <Paper sx={{ p: 2 }}>
                <Box
                    sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: theme => theme.palette.grey[100],
                        border: theme => `1px solid ${theme.palette.grey[300]}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                        >
                            <Localized id={"similarity-validation-title"}>
                                <span>_Wyniki</span>
                            </Localized>
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.25 }}
                        >
                            {useSbert && (
                                <Localized
                                    id="similarity-validation-settings-summary-sbert"
                                    vars={{
                                        min: sbertMinSimilarity || 0,
                                        top: topSbertResults || 0,
                                    }}
                                >
                                    <span>
                                        SBERT: min {sbertMinSimilarity || 0}%, top {topSbertResults || 0}
                                    </span>
                                </Localized>
                            )}
                            {useSbert && useLevenshtein && ' • '}
                            {useLevenshtein && (
                                <Localized
                                    id="similarity-validation-settings-summary-levenshtein"
                                    vars={{
                                        min: levenshteinMinSimilarity || 0,
                                        top: topLevenshteinResults || 0,
                                    }}
                                >
                                    <span>
                                        Levenshtein: min {levenshteinMinSimilarity || 0}%, top {topLevenshteinResults || 0}
                                    </span>
                                </Localized>
                            )}
                        </Typography>
                    </Box>

                    <Tooltip title={<Localized id="settings">_settings</Localized>}>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setOptionsOpen(true)}
                        >
                            <SettingsIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>


                {serverError && (
                    <Box mt={2}>
                        <Alert severity="error">{serverError}</Alert>
                    </Box>
                )}
                {!saving && results.length > 0 && (
                    <Box mt={1} sx={{
                        maxHeight: '75vh',
                        overflow: 'auto',
                    }}>

                        <Divider sx={{ mb: 1 }} />
                        <SimilarityResultsPanel
                            results={results}
                            onRequirementUpdated={(updatedReq) => {
                                setResults(prev =>
                                    prev.map(r => {
                                        const isSource = r.requirementDetail.id === updatedReq.id;

                                        const updateDetail = (d: SimilarityRequirementDetails) =>
                                            d.requirementDetail.id === updatedReq.id
                                                ? {
                                                    ...d,
                                                    requirementDetail: updatedReq,
                                                    requirementName: updatedReq.name,
                                                }
                                                : d;

                                        return {
                                            ...r,
                                            ...(isSource && {
                                                requirementDetail: updatedReq,
                                                requirementName: updatedReq.name,
                                            }),
                                            sbertSimilarityDetails: r.sbertSimilarityDetails?.map(updateDetail),
                                            levenshteinSimilarityDetails: r.levenshteinSimilarityDetails?.map(updateDetail),
                                        };
                                    })
                                );
                            }}

                            onRequirementDeleted={(deletedId) => {
                                setResults(prev =>
                                    prev
                                        .map(r => {
                                            const isSourceDeleted = r.requirementDetail.id === deletedId;

                                            const newSbert = r.sbertSimilarityDetails?.filter(
                                                d => d.requirementDetail.id !== deletedId
                                            );
                                            const newLeven = r.levenshteinSimilarityDetails?.filter(
                                                d => d.requirementDetail.id !== deletedId
                                            );

                                            if (isSourceDeleted) {
                                                return null;
                                            }

                                            return {
                                                ...r,
                                                sbertSimilarityDetails: newSbert,
                                                levenshteinSimilarityDetails: newLeven,
                                            };
                                        })
                                        .filter((r): r is UrsRequirementsSimilarityValidationResult => r !== null)
                                );
                            }}

                        />
                    </Box>
                )}


            </Paper>

            <BottomAppBar>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <Button
                        key="back"
                        onClick={handleBackButtonClick}
                        color="secondary"
                        size="small"
                        variant="outlined"
                    >
                        <Localized id="back">Wróć</Localized>
                    </Button>

                    <Button
                        color="primary"
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => setOptionsOpen(true)}
                    >
                        <Localized id="settings">_settings</Localized>
                    </Button>

                    <LoadingButton
                        loading={saving}
                        variant="contained"
                        size="small"
                        onClick={handleValidateClick}
                    >
                        <Localized id="similarity-validation-validate-button">_similarities</Localized>
                    </LoadingButton>
                </Box>
            </BottomAppBar>


            <Dialog
                open={optionsOpen}
                onClose={() => setOptionsOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Localized id="similarity-validation-dialog-title">_options</Localized>
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={1.5} mt={1}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={useSbert}
                                    onChange={(e) => setUseSbert(e.target.checked)}
                                />
                            }
                            label={<Localized id="similarity-validation-use-sbert">_sbert</Localized>}
                        />

                        {useSbert && (
                            <>
                                <TextField
                                    label={<Localized id="similarity-validation-sbert-min-label">_min</Localized>}
                                    type="number"
                                    size="small"
                                    margin="dense"
                                    fullWidth
                                    inputProps={{ step: 0.01 }}
                                    value={sbertMinSimilarity}
                                    onChange={(e) =>
                                        setSbertMinSimilarity(
                                            e.target.value === '' ? '' : Number(e.target.value),
                                        )
                                    }
                                />
                                <TextField
                                    label={<Localized id="similarity-validation-sbert-top-label">_top</Localized>}
                                    type="number"
                                    size="small"
                                    margin="dense"
                                    fullWidth
                                    value={topSbertResults}
                                    onChange={(e) =>
                                        setTopSbertResults(
                                            e.target.value === '' ? '' : Number(e.target.value),
                                        )
                                    }
                                />
                            </>
                        )}

                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={useLevenshtein}
                                    onChange={(e) => setUseLevenshtein(e.target.checked)}
                                />
                            }
                            label={<Localized id="similarity-validation-use-levenshtein">_levenshtein</Localized>}
                        />

                        {useLevenshtein && (
                            <>
                                <TextField
                                    label={<Localized id="similarity-validation-levenshtein-min-label">_min</Localized>}
                                    type="number"
                                    size="small"
                                    margin="dense"
                                    fullWidth
                                    inputProps={{ step: 0.01 }}
                                    value={levenshteinMinSimilarity}
                                    onChange={(e) =>
                                        setLevenshteinMinSimilarity(
                                            e.target.value === '' ? '' : Number(e.target.value),
                                        )
                                    }
                                />
                                <TextField
                                    label={<Localized id="similarity-validation-levenshtein-top-label">_top</Localized>}
                                    type="number"
                                    size="small"
                                    margin="dense"
                                    fullWidth
                                    value={topLevenshteinResults}
                                    onChange={(e) =>
                                        setTopLevenshteinResults(
                                            e.target.value === '' ? '' : Number(e.target.value),
                                        )
                                    }
                                />
                            </>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <CloseButton loading={false} onClick={() => setOptionsOpen(false)} />
                </DialogActions>
            </Dialog>
        </Box>

    );
};
